// src/pages/HomePage.js - Homepage component
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import { MdPlayCircleOutline, MdQuiz, MdPrint, MdEmail, MdMenuBook, MdLibraryBooks, MdCheckCircleOutline } from 'react-icons/md';

const HeroSection = styled.section`
  text-align: center;
  margin: var(--spacing-xxl) 0;
`;

const HeroTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: var(--spacing-md);
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  color: var(--text-color-light);
  max-width: 800px;
  margin: 0 auto var(--spacing-xl);
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: var(--spacing-md);
  justify-content: center;
  flex-wrap: wrap;
`;

const FeaturesSection = styled.section`
  margin: var(--spacing-xxl) 0;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  text-align: center;
  margin-bottom: var(--spacing-xl);
  color: var(--text-color);
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--spacing-xl);
  margin: var(--spacing-xl) 0;
  
  @media (min-width: 1200px) {
    margin: var(--spacing-xl) var(--spacing-lg);
  }
`;

const CTASection = styled.section`
  background-color: var(--primary-color);
  color: white;
  padding: var(--spacing-xxl) 0;
  text-align: center;
  border-radius: var(--border-radius);
  margin: var(--spacing-xxl) 0;
`;

const CTATitle = styled.h2`
  font-size: 2rem;
  margin: 0 0 var(--spacing-md) 0;
`;

const CTAText = styled.p`
  font-size: 1.25rem;
  margin: 0 auto 0 auto;
  max-width: 800px;
`;

const SectionBackground = styled.section`
  background: var(--background-light);
  padding: var(--spacing-xxl) var(--spacing-lg);
  margin: var(--spacing-xxl) 0;
  
  @media (min-width: 1200px) {
    padding: var(--spacing-xxl) var(--spacing-xxl);
  }
  
  h2, h3 {
    color: var(--text-color);
  }
  
  p {
    color: var(--text-color-light);
  }
`;

const SectionDescription = styled.p`
  text-align: center;
  color: var(--text-color-light);
  margin-bottom: var(--spacing-xl);
`;

const IconWrapper = styled.div`
  font-size: 2.5rem;
  color: var(--primary-color);
  margin-bottom: var(--spacing-md);
  display: flex;
  justify-content: center;
`;

const ModernCardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 180px;
  
  h3 {
    color: var(--text-color);
    text-align: center;
    margin-bottom: var(--spacing-sm);
  }
  
  p {
    color: var(--text-color-light);
    text-align: center;
    margin-bottom: var(--spacing-md);
  }
`;

const HomePage = () => {
  return (
    <>
      <CTASection>
        <CTATitle>Protecting Patient Information is Everyone's Responsibility</CTATitle>
        <CTAText>
          Proper handling of PHI is not just about compliance — it's about maintaining patient trust 
          and protecting sensitive information.
        </CTAText>
      </CTASection>
      <HeroSection>
        <HeroTitle>Learn Safe PHI Email Handling</HeroTitle>
        <HeroSubtitle>
          IT/Security training for proper handling of Protected Health Information (PHI) 
          in email communications for both employees and clients.
        </HeroSubtitle>
        <ButtonGroup>
          <Button as={Link} to="/tutorial" size="large">
            Start Training
          </Button>
          <Button as={Link} to="/learning?tab=checklist" variant="secondary" size="large">
            PHI Email Checklist
          </Button>
        </ButtonGroup>
      </HeroSection>
      <SectionBackground>
        <SectionTitle>Key Features</SectionTitle>
        <SectionDescription>
          Explore interactive tools and resources to master secure PHI email handling.
        </SectionDescription>
        <CardGrid>
          <Card>
            <ModernCardContent>
              <IconWrapper><MdPlayCircleOutline /></IconWrapper>
              <h3>Interactive Demonstrations</h3>
              <p>Visual step-by-step guides showing proper email encryption methods.</p>
            </ModernCardContent>
          </Card>
          <Card>
            <ModernCardContent>
              <IconWrapper><MdQuiz /></IconWrapper>
              <h3>Knowledge Assessments</h3>
              <p>Test your understanding with interactive quizzes and assess your PHI handling knowledge.</p>
            </ModernCardContent>
          </Card>
          <Card>
            <ModernCardContent>
              <IconWrapper><MdPrint /></IconWrapper>
              <h3>Printable Resources</h3>
              <p>Access printer-friendly checklists and reference materials for quick consultation.</p>
            </ModernCardContent>
          </Card>
          <Card>
            <ModernCardContent>
              <IconWrapper><MdEmail /></IconWrapper>
              <h3>Email Simulation</h3>
              <p>Practice secure email handling with our interactive Outlook-style email simulator.</p>
            </ModernCardContent>
          </Card>
        </CardGrid>
      </SectionBackground>
      <SectionBackground>
        <SectionTitle>Training Resources</SectionTitle>
        <SectionDescription>
          Access tutorials, guides, and assessments to reinforce your PHI email security skills.
        </SectionDescription>
        <CardGrid>
          <Card interactive as={Link} to="/tutorial">
            <ModernCardContent>
              <IconWrapper><MdMenuBook /></IconWrapper>
              <h3>Step-by-Step Tutorial</h3>
              <p>Learn the fundamentals of PHI handling and email encryption through our comprehensive interactive tutorials.</p>
            </ModernCardContent>
          </Card>
          <Card interactive as={Link} to="/reference">
            <ModernCardContent>
              <IconWrapper><MdLibraryBooks /></IconWrapper>
              <h3>Reference Guide</h3>
              <p>Quick access to policies, procedures, and downloadable quick-reference guides for PHI handling.</p>
            </ModernCardContent>
          </Card>
          <Card interactive as={Link} to="/learning">
            <ModernCardContent>
              <IconWrapper><MdCheckCircleOutline /></IconWrapper>
              <h3>Learning & Assessment</h3>
              <p>Interactive scenarios, knowledge quizzes, email simulations, and printable checklists to test and reinforce your skills.</p>
            </ModernCardContent>
          </Card>
        </CardGrid>
      </SectionBackground>
    </>
  );
};

export default HomePage; 